import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface ReportsSectionMetaItem {
  /** Lowercase mono key shown in the right-side meta strip. */
  key: string;
  /** Resolved value (e.g. "12h 30m" or "$1,200"). */
  value: string;
}

export interface ReportsSectionProps {
  title: string;
  count: number;
  /** Square (orange) glyph by default; pass `'circle'` for an
   *  alternate colored dot (matches the prototype's two header
   *  variants on by-project vs by-user). */
  glyph?: 'square' | 'circle';
  glyphColor?: string;
  /** Right-side meta strip — pairs of `{key, value}` shown as
   *  `key value · key value`. Empty array hides the strip. */
  meta?: ReadonlyArray<ReportsSectionMetaItem>;
  children: ReactNode;
  className?: string;
}

/**
 * Reports redesign — section frame for the by-project + by-user
 * tables. Glass card with a header (glyph + title + count pill +
 * meta strip on the right) and a children slot for the body.
 */
export const ReportsSection = ({
  title,
  count,
  glyph = 'square',
  glyphColor,
  meta,
  children,
  className,
}: ReportsSectionProps) => (
  <section
    className={cn(
      'overflow-hidden rounded-2xl border backdrop-blur-md',
      'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] shadow-[var(--glass-card-shadow)]',
      className,
    )}
  >
    <header
      className={cn(
        'flex items-center gap-2.5 border-b px-[18px] py-3.5',
        'border-[var(--glass-card-border)]',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'size-2 flex-shrink-0',
          glyph === 'circle' ? 'rounded-full' : 'rounded-[2.5px]',
        )}
        style={{ background: glyphColor ?? 'var(--brand-accent)' }}
      />
      <span
        className={cn(
          'text-foreground font-mono text-[11px] font-semibold uppercase tracking-[0.06em]',
        )}
      >
        {title}
      </span>
      <span
        className={cn(
          'text-foreground-muted rounded-full border px-[7px] py-px font-mono text-[10.5px] tabular-nums',
          'border-border bg-secondary',
        )}
      >
        {count}
      </span>
      {meta && meta.length > 0 ? (
        <span
          className={cn(
            'text-foreground-faint ml-auto inline-flex flex-wrap items-center gap-x-3.5 gap-y-1 font-mono text-[11.5px]',
          )}
        >
          {meta.map((m, i) => (
            <span key={`${m.key}-${i}`} className="inline-flex items-center gap-1.5">
              <span className="text-foreground-faint">{m.key}</span>
              <span className="text-foreground-muted font-medium">{m.value}</span>
            </span>
          ))}
        </span>
      ) : null}
    </header>
    {children}
  </section>
);
